/* eslint-disable max-len */
/**
@format
@flow
*/
import React, { useState } from 'react';
import styled from 'styled-components';
import Squircle from '@ziynx/components/squircles';
import { ImageAvatar } from '@ziynx/components/images';
import { Heading, SubHeading, Body } from '@ziynx/components/typography';
import { Popover } from '@ziynx/components/popovers';
import Colors from '@ziynx/theme/colors';
import Link from '@ziynx/components/links';
import Icon from '@ziynx/components/icons';
import Headline from '@ziynx/components/headlines';
import { withAppManager } from '@ziynx/components/utilities';
import type { SquircleType, RightActionType } from '@ziynx/flow-types/globals';
import type { Headline as HeadlineType } from '@ziynx/flow-types/headlines';
import type { ManagedProps } from '@ziynx/flow-types/appManager';
import CardBase from './CardBase';

export type Props = $ReadOnly<{|
  /**
   * To determine fixed dimensions for the card
   */
  fixedWidth?: boolean,
  /**
   * If fixed is true, size is set.
   */
  size?: 'extraSmall' | 'small' | 'medium' | 'large',
  /**
   * Headline object. Optional
   */
  headline?: HeadlineType,
  /**
   * Squircle name
   */
  squircle?: SquircleType,
  /**
   * Icon right name
   */
  rightAction?: RightActionType,
  /**
   * Title for the card
   */
  title?: string,
  /**
   * Subtitle for the card
   */
  subtitle?: string,
  /**
   * Color for the subtitle
   */
  subtitleColor: string,
  /**
   * Text for the notification, if any
   */
  notificationText?: string,
  /**
   * Body for the card
   */
  body?: string,
  /**
   * Archived prop
   */
  archived?: boolean,

|}>;

const StyledCardBase = styled(CardBase)`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  align-items: center;
  justify-content: start;
  padding: 16px 24px;
  min-width: 288px;
  max-width: 688px;
  min-height: 108px;
  box-sizing: border-box;
`;

const StyledIconSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
  width: 100%;
  padding-bottom: 12px;
`;

const SquircleWrapper = styled.div`
  display: flex;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ isSmall }) => !isSmall && 'padding: 0 32px;'};
`;

const StyledHeading = styled(Heading)`
  display: -webkit-box;
  -webkit-line-clamp: 3; 
  -webkit-box-orient: vertical; 
  max-height: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledSubHeading = styled(SubHeading)`
  display: -webkit-box;
  -webkit-line-clamp: 2; 
  -webkit-box-orient: vertical; 
  max-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledBody = styled(Body)`
  padding-top: 4px;
  ${({ subtitle, isMobileSmall }) => subtitle
  && `
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  max-height: ${isMobileSmall ? '16px' : '22px'};
  overflow: hidden;
  text-overflow: ellipsis;`
}
`;

const StyledNotify = styled.span`
  margin-left: 4px;
`;

const HeadlineWrapper = styled.div`
  max-width: 688px;
  padding-bottom: 24px;
`;

const RightCTAWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  box-sizing: border-box;
`;

const StyledLink = styled(Link)`
  text-align: center;
  width: 16px;
  height: 16px;
`;

const BodyWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    box-sizing: border-box;
    min-width: 80px;
`;

const StyledDot = styled.div`
    padding: 0px 8px;
    color: ${Colors.Gray};
`;

const ArchivedIconWrapper = styled.div`
    margin-right: 24px;
`;

const ArchivedPopover = styled.div``;

function _getWidth(widthProps: $ReadOnly<{
  fixedWidth?: boolean,
  size?: 'extraSmall' | 'small' | 'medium' | 'large',
}>) {
  const {
    fixedWidth, size,
  } = widthProps || {};

  if (!fixedWidth) return '100%';

  let width = '';
  if (size === 'large') {
    width = '688px';
  } else if (size === 'medium') {
    width = '520px';
  } else if (size === 'small') {
    width = '382px';
  } else if (size === 'extraSmall') {
    width = '288px';
  }

  return width;
}

const CardSquircle = (props: ManagedProps<Props>): React$Node => {
  const {
    viewport,
    fixedWidth,
    size,
    title,
    squircle,
    rightAction,
    subtitle,
    subtitleColor,
    notificationText,
    body,
    archived,
    headline,
    ...rest
  } = props;

  const [archiveTooltip, setArchiveTooltip] = useState(false);

  const isMobileSmall = viewport === 'mobileSmall';

  const isFixedSmall = fixedWidth && (size === 'extraSmall' || size === 'small');

  const CARD_WIDTH = _getWidth(props);

  const _renderSquircle = (): React$Node => {
    const {
      iconName, invertedColor, imageSource,
      size: squircleSize, squircleText, inverted, ...restSquircleProps
    } = squircle || {};

    return (
      <SquircleWrapper>
        {!imageSource && (
          <Squircle
            {...restSquircleProps}
            {...{ text: squircleText }}
            inverted={inverted}
            size={(isMobileSmall || isFixedSmall) ? 'extraSmall' : 'medium'}
            iconName={iconName}
            invertedColor={invertedColor || Colors.GrayVeryDark}
          />
        )}
        {!!imageSource && (
          <ImageAvatar
            size={(isMobileSmall || isFixedSmall) ? 'extraSmall' : 'medium'}
            imageSource={imageSource}
          />
        )}
      </SquircleWrapper>
    );
  };

  const _renderRightCTA = (): React$Node => {
    const {
      name, color, onClick, popover, ...restRightActionProps
    } = rightAction || {};

    const {
      links, children, position, ...restPopoverProps
    } = popover || {};

    return (
      <RightCTAWrapper>
        {archived && !isMobileSmall && (
          <Popover
            position="bottomLeft"
            open={archiveTooltip}
            trigger={(
              <ArchivedIconWrapper
                onMouseDown={viewport === 'mobile' ? () => setArchiveTooltip(!archiveTooltip) : undefined}
                onMouseOver={viewport !== 'mobile' ? () => setArchiveTooltip(true) : undefined}
                onMouseOut={viewport !== 'mobile' ? () => setArchiveTooltip(false) : undefined}
              >
                <Icon extraSmall name="archive-fill" color={Colors.Gray} />
              </ArchivedIconWrapper>
            )}
          >
            <ArchivedPopover>
              <Body medium>
                Archived
              </Body>
            </ArchivedPopover>
          </Popover>
        )}
        {(links && links?.length && !onClick) ? (
          <Popover
            {...restPopoverProps}
            position={position || 'bottomRight'}
            links={links}
            trigger={(
              <StyledLink variant="inline">
                <Icon
                  {...restRightActionProps}
                  veryTiny
                  name={name || 'more'}
                  color={color || Colors.Gray}
                />
              </StyledLink>
            )}
          >
            {children}
          </Popover>
        ) : (
          <StyledLink variant="inline" onClick={onClick}>
            <Icon
              {...restRightActionProps}
              veryTiny
              name={name || 'more'}
              color={color || Colors.Gray}
            />
          </StyledLink>

        )}
      </RightCTAWrapper>

    );
  };

  const _renderTextSection = (): React$Node => (
    <TextWrapper isSmall={isMobileSmall || isFixedSmall}>
      {(isMobileSmall || isFixedSmall)
        ? (
          <StyledSubHeading medium color={Colors.GrayVeryDark}>
            {title}
          </StyledSubHeading>
        )
        : (
          <StyledHeading small color={Colors.GrayVeryDark}>
            {title}
          </StyledHeading>
        )}
      {subtitle && (
        <StyledBody
          medium={!isMobileSmall && !isFixedSmall}
          extraSmall={isMobileSmall || isFixedSmall}
          color={subtitleColor || Colors.GrayDark}
          subtitle
          isMobileSmall={isMobileSmall}
        >
          {subtitle}
          {notificationText && (
            <StyledNotify>
              <Body
                primitive="span"
                {...(!isFixedSmall && { medium: true })}
                color={Colors.SecondaryRed}
              >
                {notificationText}

              </Body>
            </StyledNotify>
          )}
        </StyledBody>
      )}
      {body && (
        <BodyWrapper>
          <StyledBody
            medium={!isMobileSmall && !isFixedSmall}
            extraSmall={isMobileSmall || isFixedSmall}
            color={Colors.GrayDark}
          >
            {body}
          </StyledBody>
          {archived && isMobileSmall && <StyledDot>&#183;</StyledDot>}
          {archived && isMobileSmall && <StyledBody extraSmall color={Colors.GrayDark}>Archived</StyledBody>}
        </BodyWrapper>
      )}
    </TextWrapper>

  );

  return (
    <>
      {headline && (headline?.title?.text || headline?.children) && (
        <HeadlineWrapper>
          <Headline {...headline} width={CARD_WIDTH} />
        </HeadlineWrapper>

      )}
      <StyledCardBase
        width={CARD_WIDTH}
        isMobile={isMobileSmall || isFixedSmall}
        {...rest}
      >
        {(isMobileSmall || isFixedSmall) && (
          <>
            <StyledIconSection>
              {_renderSquircle()}
              {_renderRightCTA()}
            </StyledIconSection>
            {_renderTextSection()}
          </>
        )}
        {(!isMobileSmall && !isFixedSmall) && (
          <>
            {_renderSquircle()}
            {_renderTextSection()}
            {_renderRightCTA()}
          </>
        )}
      </StyledCardBase>
    </>
  );
};

export default (withAppManager(CardSquircle): React$ComponentType<ManagedProps<Props>>);
